import React, { useState, useContext } from 'react';
import { BackMatter, BookStructureTextElementKey } from '../types';
import { BOOK_SIZES, getDefaultBookSize } from '@shared/book-size-constants';
import { labelClass, textareaClass, inputClass, primaryButtonClass, secondaryButtonClass } from '../constants';
import ImageUpload from './ImageUpload';
import { CoverImageWithFallback } from './CoverImageWithFallback';
import { AppContext } from '../contexts/AppContext';
import { marked } from 'marked';
import { useBackMatterAI } from '../hooks/useBackMatterAI'; // Import the new hook

interface BackMatterEditorProps {
    backMatter: BackMatter;
    onBackMatterChange: <K extends keyof BackMatter>(field: K, value: BackMatter[K]) => void;
    onAddListItem: (listField: 'appendices' | 'glossary' | 'bibliography') => void;
    onDeleteListItem: (listField: 'appendices' | 'glossary' | 'bibliography', id: string) => void;
    onUpdateListItem: (...args: any) => void;
}

const BackMatterEditor: React.FC<BackMatterEditorProps> = ({ backMatter, onBackMatterChange }) => {
    const appContext = useContext(AppContext);
    const { generateBackMatterElement, isLoading: isAILoading } = useBackMatterAI(); // Use the hook

    const isLoadingBackCoverImageAI = appContext?.isLoading['generateBackCoverImage'];
    const [previewContent, setPreviewContent] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState<string | null>(null);
    
    // Define fields with their corresponding AI generation type
    const fields: Array<{ key: keyof Omit<BackMatter, 'backCoverImageBase64' | 'backCoverImagePrompt' | 'backCoverImageAspectRatio' | 'backCoverImageGeneratedBase64' | 'appendices' | 'glossary' | 'bibliography' | 'aboutTheAuthor' | 'contactSocialMedia'>; label: string; placeholder: string; aiGenType?: BookStructureTextElementKey }> = [
        { key: 'epilogue', label: 'Epilogue', placeholder: 'Enter epilogue content...', aiGenType: 'epilogue' },
        { key: 'afterword', label: 'Afterword', placeholder: 'Enter afterword content...', aiGenType: 'afterword' },
        { key: 'authorsNote', label: 'Author\'s Note', placeholder: 'Enter author\'s note...', aiGenType: 'authorsNote' },
        { key: 'acknowledgmentsBack', label: 'Acknowledgments (Back)', placeholder: 'Enter acknowledgments if placed in back matter...', aiGenType: 'acknowledgmentsBack' },
        { key: 'readingGroupGuide', label: 'Reading Group Guide', placeholder: 'Enter discussion questions...', aiGenType: 'readingGroupGuide' },
        { key: 'teaserNextBook', label: 'Teaser for Next Book', placeholder: 'Enter teaser content...', aiGenType: 'teaserNextBook' },
        { key: 'index', label: 'Index', placeholder: 'Enter index terms...', aiGenType: 'index' },
    ];

    const handleImageUpload = (base64Image: string | null) => {
        onBackMatterChange('backCoverImageBase64', base64Image);
    };
    
    const showPreview = (title: string, contentKey: keyof BackMatter) => {
        setPreviewTitle(title);
        const mdContent = backMatter[contentKey] as string || '';
        try {
            const result = marked.parse(mdContent); // Use marked.parse for synchronous parsing
            setPreviewContent(result as string); // Cast to string as marked.parse returns string | Promise<string>
        } catch (error) {
            console.error("Error parsing markdown:", error);
            setPreviewContent(mdContent); // Fallback to original content
        }
    };
    
    const handleGenerateBackCoverImage = () => {
        if (!appContext) return;
        const projectState = appContext.projectState;
        if (!projectState) return;
        appContext.openImageGenerationModalFromContext('backCover', {
            logLine: projectState.blueprint?.worldBuildingSuggestions?.logLine || '',
            genre: projectState.bookMetadata?.genreSubject || 'General',
            overallMood: projectState.bookMetadata?.toneAndMood || 'Intriguing',
            bookTitle: projectState.bookMetadata?.title || '',
            themes: projectState.blueprint?.worldBuildingSuggestions?.themes || [],
        });
    };

    const handleGenerateBackCoverImageFromPrompt = () => {
        if (!appContext) return;
        appContext.openImageGenerationModalFromContext('backCover', backMatter.backCoverImagePrompt || '');
    };

    return (
        <div className="space-y-6 p-1 h-full flex gap-4">
            <div className="w-2/3 overflow-y-auto pr-2 space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-4">Back Matter Elements</h3>
                <p className="text-sm text-gray-500 -mt-2 mb-4">Manage content that appears after your main story. Text fields support Markdown.</p>
                
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Author information (bio, contact, social media) is now managed in the 
                        <strong> Author Details</strong> section and will automatically appear in your book's back matter.
                    </p>
                </div>
                
                 <div className="md:col-span-2 p-4 border rounded-md bg-gray-50 shadow-sm">
                     <ImageUpload
                        label="Back Cover Image (Optional)"
                        currentImage={backMatter.backCoverImageBase64}
                        onImageUpload={handleImageUpload}
                        recommendedSize="e.g., Cover aspect ratio"
                    />
                    <button
                        onClick={handleGenerateBackCoverImage}
                        disabled={isLoadingBackCoverImageAI || !appContext?.apiKey}
                        className={`${secondaryButtonClass} text-xs mt-2`}
                        title={!appContext?.apiKey ? "API Key required" : "Generate Back Cover Image using AI"}
                    >
                        {isLoadingBackCoverImageAI ? 'Generating Image...' : 'Generate Back Cover Image (AI)'}
                    </button>
                </div>

                {/* Back Cover Image Generation Section */}
                <div className="md:col-span-2 p-4 border rounded-md bg-purple-50 shadow-sm">
                    <h4 className="text-lg font-medium text-gray-800 mb-3">🎨 Back Cover Image Generation</h4>
                    <p className="text-sm text-gray-600 mb-4">Generate a custom back cover image for your book with AI.</p>
                    
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="backCoverImagePrompt" className={`${labelClass} mb-1.5`}>Image Description:</label>
                            <textarea
                                id="backCoverImagePrompt"
                                value={backMatter.backCoverImagePrompt || ''}
                                onChange={(e) => onBackMatterChange('backCoverImagePrompt', e.target.value)}
                                placeholder="Describe the back cover image you want (e.g., 'A mysterious silhouette against a moonlit landscape')"
                                className={`${textareaClass} min-h-[4rem]`}
                                rows={3}
                            />
                        </div>
                        
                        <div>
                            <label htmlFor="backCoverImageAspectRatio" className={`${labelClass} mb-1.5`}>Book Size & Format:</label>
                            <select
                                id="backCoverImageAspectRatio"
                                value={backMatter.backCoverImageAspectRatio || getDefaultBookSize().ratio}
                                onChange={(e) => onBackMatterChange('backCoverImageAspectRatio', e.target.value)}
                                className={inputClass}
                            >
                                {BOOK_SIZES.map(category => (
                                    <optgroup key={category.category} label={category.label}>
                                        {category.sizes.map(size => (
                                            <option key={size.key} value={size.ratio}>
                                                {size.label} - {size.description}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Select a realistic book size for professional back cover image generation. Dimensions shown are width × height.</p>
                        </div>

                        {backMatter.backCoverImageGeneratedBase64 && (
                            <div>
                                <label className={`${labelClass} mb-2`}>Generated Back Cover Image:</label>
                                <CoverImageWithFallback
                                    src={backMatter.backCoverImageGeneratedBase64}
                                    alt="Generated Back Cover Image"
                                    className="w-full max-w-md rounded-lg border border-gray-300"
                                />
                            </div>
                        )}

                        <div className="flex gap-2">
                            <button
                                onClick={handleGenerateBackCoverImageFromPrompt}
                                disabled={!backMatter.backCoverImagePrompt?.trim()}
                                className={`${secondaryButtonClass} flex-1`}
                                title="Generate back cover image with AI based on your description"
                            >
                                Generate Back Cover Image (AI)
                            </button>
                            {backMatter.backCoverImageGeneratedBase64 && (
                                <button
                                    onClick={() => onBackMatterChange('backCoverImageGeneratedBase64', null)}
                                    className={`${primaryButtonClass}`}
                                    title="Remove generated image"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {fields.map(({ key, label, placeholder, aiGenType }) => (
                    <div key={key} className="p-4 border rounded-md bg-gray-50 shadow-sm">
                        <label htmlFor={`bm-${String(key)}`} className={`${labelClass} mb-1.5 text-lg font-medium`}>{label}:</label>
                        <textarea
                            id={`bm-${String(key)}`}
                            value={String(backMatter[key as keyof BackMatter] || '')}
                            onChange={(e) => onBackMatterChange(key as (string & keyof BackMatter), e.target.value as any)}
                            placeholder={placeholder}
                            className={`${textareaClass} min-h-[6rem]`}
                        />
                         <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-gray-500">Supports Markdown.</p>
                            <div>
                                {aiGenType && (
                                    <button
                                        onClick={() => generateBackMatterElement(aiGenType)}
                                        disabled={isAILoading || !appContext?.apiKey}
                                        className={`${secondaryButtonClass} text-xs mr-2`}
                                        title={!appContext?.apiKey ? "API Key required" : `Generate ${label} with AI`}
                                    >
                                        {isAILoading ? `Generating...` : `Generate (AI)`}
                                    </button>
                                )}
                                <button 
                                    onClick={() => showPreview(label, key as keyof BackMatter)}
                                    className={`${primaryButtonClass} text-xs`}
                                >
                                    Preview
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Here you would map over appendices, glossary, etc. and render editors for them */}

            </div>

             <div className="w-1/3 p-4 border-l border-gray-200 overflow-y-auto bg-white rounded-r-lg shadow-inner">
                <h3 className="text-lg font-semibold text-gray-700 mb-3 sticky top-0 bg-white pb-2 border-b">
                    {previewTitle ? `Preview: ${previewTitle}` : "Content Preview"}
                </h3>
                {previewContent ? (
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewContent }} />
                ) : (
                    <p className="text-gray-500 text-sm">Click a "Preview" button on an element to see its rendered Markdown here.</p>
                )}
            </div>
        </div>
    );
};

export default BackMatterEditor;